import { Component, Inject, ContentChildren, QueryList, AfterContentInit, OnInit, OnDestroy } from '@angular/core';
import { Observable, merge, of, Subject } from 'rxjs';
import { Router, GuardsCheckEnd } from '@angular/router';
import { auditTime, filter, map, share, startWith, tap } from 'rxjs/operators';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { IdentityService } from 'src/app/core/identity.service';
import { MenuService } from 'src/app/core/menu.service';
import { MenuItemDirective } from 'src/app/nav-menu/menu-item.directive';
import { MatDialog } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { CandidateSearchModalComponent } from 'src/app/search-all-candidates/candidate-search-modal/candidate-search-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { selectUnreadInAppNotificationCounts } from 'src/app/in-app-notifications/store/selectors/in-app-notifications.selectors';
import { selectRecruitingDealAlertData } from '../root-store/recruiting-store/selectors/deals.selector';
import { UnreadInAppNotificationCounts } from 'src/app/in-app-notifications/models/unread-in-app-notification-counts.model';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { NavMenuLinkComponent } from 'src/app/nav-menu/nav-menu-link.component';
import { VersionComponent } from 'src/app/shared/components/version/version.component';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';

enum MODULES {
    WELCOME = 'welcome',
    REPORTING = 'reporting',
    CANDIDATE_SEARCH = '_recruiting',
    IN_APP_NOTIFICATIONS = 'in-app-notifications',
    WFD_AGENT = 'wfd-agent'
}

@UntilDestroy()
@Component({
    selector: 'nova-nav-menu',
    standalone: true,
    imports: [
        MatIconModule,
        MatListModule,
        MatButtonModule,
        MatBadgeModule,
        AsyncPipe,
        NavMenuLinkComponent,
        VersionComponent,
        NgOptimizedImage
    ],
    templateUrl: './nav-menu.component.html',
    styleUrls: ['./_styles.scss', './nav-menu.component.scss']
})
export class NavMenuComponent implements OnInit, AfterContentInit, OnDestroy {
    isCollapsed = false;

    domain: string;

    userId = 0;

    aspNetUserId: string;

    organizationId = 0;

    openedModule: string;

    activeModule: string;

    activeModuleItem: string;

    menuModules$: Observable<MenuItemDirective[]>;

    hidden = false;

    dealAlert = 0;

    // Only top-level menu modules are registered with MenuService.
    // Descendant items are traversed through each root item's subItems.
    @ContentChildren(MenuItemDirective)
    private readonly _rootMenuItems: QueryList<MenuItemDirective>;

    // Watch all descendants so feature-flag @if blocks (nested items) trigger rebuilds.
    @ContentChildren(MenuItemDirective, { descendants: true })
    private readonly _allMenuItems: QueryList<MenuItemDirective>;

    readonly moduleTypes = MODULES;

    readonly openedModuleSessionStorageKey = 'openedModule';
    moduleIsClicked: string;

    private readonly _cleanup$ = new Subject();

    // Emits rebuilt visible menu modules after content/query-list changes.
    private readonly _menuRefresh$ = new Subject<MenuItemDirective[]>();

    unreadMessagesCount = 0;

    get showInAppNotifications(): boolean {
        return this.aspNetUserId && this.isLoggedIn;
    }

    constructor(
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _identityService: IdentityService,
        private readonly _router: Router,
        private readonly _menuService: MenuService,
        public dialog: MatDialog,
        private readonly overlay: Overlay,
        private readonly _mediaObserver: BreakpointObserver,
        private readonly _store: Store
    ) {
        this.domain = this._settings.domain;
        this.organizationId = this._settings.organizationId;
        //Show menu as hidden:true from the beggining to avoid flicker for a second with full screen login page.
        this.hidden = true;
    }

    ngOnInit(): void {
        this._mediaObserver
            .observe(['xs', 'md', 'sm', 'lt-md'])
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result.matches) {
                    this.isCollapsed = true;
                } else {
                    this.isCollapsed = false;
                }
            });

        this.menuModules$ = merge(
            of([]),
            this._menuRefresh$,
            this._router.events.pipe(
                filter((event) => event instanceof GuardsCheckEnd),
                tap((event: GuardsCheckEnd) => {
                    this.hidden = this._menuService.isHiddenMenuRoute(event.urlAfterRedirects);
                }),
                map((event: GuardsCheckEnd) => {
                    this.userId = 0;
                    this.dealAlert = null;
                    const identity = this._identityService.current;

                    if (identity && !MenuService.publicRoutes.includes(event.url)) {
                        this.userId = identity.contactId;
                        this.aspNetUserId = identity.userId;
                        if (this.userId) {
                            this._store
                                .select(selectRecruitingDealAlertData)
                                .pipe(share(), untilDestroyed(this))
                                .subscribe((data) => {
                                    if (data) {
                                        if (data.deals && data.deals > 0 && data.recruiterId === this.userId) {
                                            this.dealAlert = data.deals;
                                        }
                                    }
                                });
                        }
                        if (this.aspNetUserId) {
                            this._store
                                .select(selectUnreadInAppNotificationCounts)
                                .pipe(share(), untilDestroyed(this))
                                .subscribe((unreadInAppNotificationCounts: UnreadInAppNotificationCounts) => {
                                    this.unreadMessagesCount = unreadInAppNotificationCounts.unreadNotificationCount;
                                    this.unreadMessagesCount +=
                                        unreadInAppNotificationCounts.overdueTasksAssignedToMeCount;
                                });
                        }
                        const item = this._menuService.urlToModuleItem(event.url);
                        this.activeModule = item.moduleName;
                        this.activeModuleItem = item.itemName;
                        this.openedModule = this.activeModuleItem
                            ? sessionStorage.getItem(this.openedModuleSessionStorageKey)
                            : null;
                        if (event.url.includes('/in-app-notifications')) {
                            this.moduleIsClicked = this.moduleTypes.IN_APP_NOTIFICATIONS;
                        } else {
                            this.moduleIsClicked = this.openedModule;
                        }

                        return this._menuService.getVisibleItems(identity);
                    }
                    return [];
                })
            )
        );
    }

    ngAfterContentInit(): void {
        this._allMenuItems.changes
            .pipe(
                startWith(this._allMenuItems),
                auditTime(0), // Wait one microtask so nested QueryLists are fully stabilized before rebuild.
                untilDestroyed(this)
            )
            .subscribe(() => {
                // Clear stale registrations before re-registering root modules.
                this._menuService.reset();

                // Register roots only; MenuService derives descendants from each root's subItems.
                this._rootMenuItems.forEach((item) => this._menuService.register(item));

                // force menu stream update when @if branches add/remove menu items
                const identity = this._identityService.current;
                if (identity && !MenuService.publicRoutes.includes(this._router.url)) {
                    // Push recalculated visible items so async menu stream updates immediately.
                    this._menuRefresh$.next(this._menuService.getVisibleItems(identity));
                } else {
                    // If there is no authenticated/eligible context, clear rendered menu modules.
                    this._menuRefresh$.next([]);
                }
            });
    }

    ngOnDestroy(): void {
        this._menuRefresh$.complete();
        this._cleanup$.next(true);
        this._cleanup$.complete();
    }

    get isLoggedIn(): boolean {
        return this.userId > 0;
    }

    openModule(moduleName: string): void {
        this.moduleIsClicked = moduleName;
        if (moduleName === this.openedModule) {
            this.openedModule = null;
        } else {
            this.openedModule = moduleName;
        }
        sessionStorage.setItem(this.openedModuleSessionStorageKey, this.openedModule);
        if (moduleName === this.moduleTypes.WELCOME) {
            this._router.navigate(['/welcome/home']);
        } else if (moduleName === this.moduleTypes.REPORTING) {
            this._router.navigate(['/reporting/reports']);
        } else if (moduleName === this.moduleTypes.CANDIDATE_SEARCH) {
            this.openModal();
        } else if (moduleName === this.moduleTypes.IN_APP_NOTIFICATIONS) {
            this._router.navigate(['/in-app-notifications']);
        } else if (moduleName === this.moduleTypes.WFD_AGENT) {
            this._router.navigate(['/wfd-agent']);
        }
    }

    toggleSidebar(): void {
        this.isCollapsed = !this.isCollapsed;
    }

    openModal() {
        const scrollStrategy = this.overlay.scrollStrategies.reposition();
        this.dialog.open(CandidateSearchModalComponent, {
            position: {
                left: '250px'
            },
            maxWidth: '100%',
            panelClass: ['search-modal-panel', 'nova__dialog'],
            scrollStrategy
        });
    }
}
